export const PROPERTY_IMAGE_FALLBACK = '/assets/img/home1/tour-package-img1.jpg'

const DEFAULT_SHARING_OPTIONS = [1, 2, 3]

const toStringValue = (value, fallback = '') =>
  typeof value === 'string' && value.trim() ? value.trim() : fallback

const toTextValue = (value, fallback = '') => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim()
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }
  return fallback
}

const toNumberValue = (value, fallback = 0) => {
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : fallback
}

const toBooleanValue = (value, fallback = false) => {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'number') {
    return value > 0
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') return true
    if (normalized === 'false') return false
  }
  return fallback
}

const toArrayValue = (value) => {
  if (Array.isArray(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim()) {
    return [value.trim()]
  }
  return []
}

const toStringArray = (value, fallback = []) => {
  const normalized = toArrayValue(value)
    .map((item) => toStringValue(item))
    .filter(Boolean)

  return normalized.length > 0 ? [...new Set(normalized)] : fallback
}

const toNumberArray = (value, fallback = []) => {
  const normalized = toArrayValue(value)
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item) && item > 0)

  return normalized.length > 0 ? [...new Set(normalized)] : fallback
}

const slugify = (value) =>
  toStringValue(value)
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const normalizeHtmlString = (value) => (typeof value === 'string' ? value.trim() : '')

const normalizeExternalLink = (value) => {
  const candidate = toTextValue(value)
  if (!candidate) {
    return ''
  }

  if (/^https?:\/\//i.test(candidate)) {
    return candidate
  }

  if (/^[\w.-]+\.[a-z]{2,}\//i.test(candidate) || /^[\w.-]+\.[a-z]{2,}$/i.test(candidate)) {
    return `https://${candidate}`
  }

  return ''
}

const isImageUrlLike = (value) => /^(https?:\/\/|\/|data:image\/)/i.test(String(value || '').trim())

const toImageUrl = (value) => {
  const text = toTextValue(value)
  if (!text || !isImageUrlLike(text)) {
    return ''
  }
  return text
}

const normalizeImageList = (values = []) =>
  [...new Set(
    toArrayValue(values)
      .map((item) => toImageUrl(item))
      .filter(Boolean)
  )]

const extractGalleryImages = (rawProperty = {}) => {
  const rawGalleries = toArrayValue(rawProperty.galleries ?? rawProperty.gallery)

  return normalizeImageList(
    rawGalleries.flatMap((gallery) => {
      if (typeof gallery === 'string') {
        return [gallery]
      }

      if (!gallery || typeof gallery !== 'object') {
        return []
      }

      return [
        gallery.imageURL,
        gallery.imageUrl,
        gallery.url,
        gallery.href,
        gallery.thumbnail_url,
        gallery.thumbnailUrl
      ]
    })
  )
}

const normalizeAmenityGroups = (rawProperty = {}) => {
  const rawAmenities = toArrayValue(rawProperty.amenities)
  if (rawAmenities.length === 0) {
    return []
  }

  const normalizeAmenityItem = (item, index) => {
    if (typeof item === 'string') {
      const name = toTextValue(item)
      return name
        ? {
            id: slugify(name) || `amenity-${index + 1}`,
            name
          }
        : null
    }

    if (!item || typeof item !== 'object') {
      return null
    }

    const name = toTextValue(item.name ?? item.title)
    if (!name) {
      return null
    }

    return {
      id: toTextValue(item.amenity_id ?? item.id, slugify(name) || `amenity-${index + 1}`),
      name
    }
  }

  const allStrings = rawAmenities.every((item) => typeof item === 'string')
  if (allStrings) {
    const items = rawAmenities
      .map((item, index) => normalizeAmenityItem(item, index))
      .filter(Boolean)

    return items.length > 0
      ? [
          {
            id: 'amenities',
            name: 'Amenities',
            items
          }
        ]
      : []
  }

  return rawAmenities
    .map((group, groupIndex) => {
      if (!group || typeof group !== 'object') {
        return null
      }

      const groupName = toTextValue(group.name ?? group.title, `Amenities ${groupIndex + 1}`)
      const children = toArrayValue(group.childrens ?? group.children ?? group.items)

      const items = children
        .map((item, itemIndex) => normalizeAmenityItem(item, itemIndex))
        .filter(Boolean)

      // Some payloads may send a flat amenity object without children.
      if (items.length === 0) {
        const fallbackItem = normalizeAmenityItem(group, 0)
        if (fallbackItem) {
          items.push(fallbackItem)
        }
      }

      if (items.length === 0) {
        return null
      }

      return {
        id: toTextValue(group.amenity_id ?? group.id, slugify(groupName) || `amenity-group-${groupIndex + 1}`),
        name: groupName,
        items
      }
    })
    .filter(Boolean)
}

const flattenAmenityGroups = (amenityGroups = []) =>
  amenityGroups.flatMap((group) => group.items?.map((item) => item.name) || []).filter(Boolean)

const normalizeNearByPlaceGroups = (rawProperty = {}) =>
  toArrayValue(rawProperty.near_by_places)
    .map((group, groupIndex) => {
      if (!group || typeof group !== 'object') {
        return null
      }

      const title = toTextValue(group.title ?? group.name, `Nearby Group ${groupIndex + 1}`)
      const places = toArrayValue(group.places)
        .map((place, placeIndex) => {
          if (!place || typeof place !== 'object') {
            return null
          }

          const placeTitle = toTextValue(place.title ?? place.name, `Place ${placeIndex + 1}`)
          if (!placeTitle) {
            return null
          }

          const latitude = toNumberValue(place.location?.latitude ?? place.latitude, null)
          const longitude = toNumberValue(place.location?.longitude ?? place.longitude, null)

          return {
            id: toTextValue(place.near_by_place_id ?? place.id, slugify(placeTitle) || `place-${placeIndex + 1}`),
            title: placeTitle,
            distanceInKm: toTextValue(place.distance_in_km ?? place.distanceInKm),
            description: toTextValue(place.description),
            mapLink: normalizeExternalLink(place.google_map_link ?? place.googleMapLink),
            coordinates: latitude !== null && longitude !== null
              ? { latitude, longitude }
              : null
          }
        })
        .filter(Boolean)

      if (places.length === 0) {
        return null
      }

      return {
        id: slugify(title) || `nearby-group-${groupIndex + 1}`,
        title,
        places
      }
    })
    .filter(Boolean)

const extractGalleryImageUrls = (galleries = []) =>
  toArrayValue(galleries).flatMap((item) => {
    if (typeof item === 'string') return [item]
    if (!item || typeof item !== 'object') return []
    return [item.imageURL, item.imageUrl, item.url, item.href]
  })

const normalizePlanImages = (rawPlan = {}) =>
  normalizeImageList(
    extractGalleryImageUrls(rawPlan.images ?? rawPlan.galleries ?? rawPlan.photos)
  )

/**
 * Build a lookup map from the separate `roomTypePlanGalleries` API field.
 * Each entry maps a roomTypePlanId → { images, name, thumbnailUrl } (plan-level + room-level).
 */
const buildPlanGalleryMap = (rawProperty = {}) => {
  const galleryEntries = toArrayValue(
    rawProperty.roomTypePlanGalleries ?? rawProperty.room_type_plan_galleries
  )

  const map = {}

  for (const entry of galleryEntries) {
    if (!entry || typeof entry !== 'object') continue

    const planId = toStringValue(entry.roomTypePlanId ?? entry.room_type_plan_id)
    if (!planId) continue

    // Plan-level galleries
    const planImages = extractGalleryImageUrls(entry.galleries)

    // Room-level galleries (each room can have its own gallery)
    const roomImages = toArrayValue(entry.rooms).flatMap((room) => {
      if (!room || typeof room !== 'object') return []
      return extractGalleryImageUrls(room.galleries)
    })

    const allImages = [...planImages, ...roomImages]

    map[planId] = {
      images: allImages,
      name: toTextValue(entry.name ?? entry.plan_name),
      thumbnailUrl: toTextValue(entry.thumbnail_url ?? entry.thumbnailUrl)
    }
  }

  return map
}

/**
 * Create plan entries from roomTypePlanGalleries when roomTypePlans is empty.
 * This happens when the API only populates gallery data without separate plan records.
 */
const buildPlansFromGalleries = (galleryMap) => {
  return Object.entries(galleryMap).map(([planId, entry], index) => {
    const images = normalizeImageList(entry.images)

    return {
      id: planId,
      title: entry.name || `Plan ${index + 1}`,
      sharingLabel: '',
      description: '',
      monthlyPrice: 0,
      originalPrice: 0,
      amenities: [],
      images: images.length > 0 ? images : [PROPERTY_IMAGE_FALLBACK]
    }
  })
}

const normalizeRoomTypePlans = (rawProperty = {}) => {
  const galleryMap = buildPlanGalleryMap(rawProperty)
  const rawPlans = toArrayValue(rawProperty.roomTypePlans ?? rawProperty.room_type_plans)

  // When roomTypePlans is empty but roomTypePlanGalleries has data,
  // create plans from the gallery entries.
  if (rawPlans.length === 0 && Object.keys(galleryMap).length > 0) {
    return buildPlansFromGalleries(galleryMap)
  }

  return rawPlans
    .map((plan, index) => {
      if (!plan || typeof plan !== 'object') {
        return null
      }

      const planId = toTextValue(plan.room_type_plan_id ?? plan.roomTypePlanId ?? plan.id, `plan-${index + 1}`)

      const title = toTextValue(
        plan.plan_name ??
          plan.planName ??
          plan.room_type_name ??
          plan.roomTypeName ??
          plan.name,
        `Plan ${index + 1}`
      )
      const sharingLabel = toTextValue(
        plan.sharing_label ??
          plan.sharingLabel ??
          plan.sharing_type ??
          plan.sharingType ??
          plan.occupancy ??
          plan.sharing
      )
      const description = toTextValue(plan.description)
      const monthlyPrice = toNumberValue(
        plan.monthly_price ??
          plan.monthlyPrice ??
          plan.price ??
          plan.amount ??
          plan.rent,
        0
      )
      const originalPrice = toNumberValue(
        plan.original_price ??
          plan.originalPrice ??
          plan.compare_at_price ??
          plan.compareAtPrice,
        0
      )

      const planAmenities = toArrayValue(plan.amenities ?? plan.additional_services ?? plan.additionalServices)
        .map((item) => {
          if (typeof item === 'string') {
            return toTextValue(item)
          }

          if (!item || typeof item !== 'object') {
            return ''
          }

          return toTextValue(item.name ?? item.title)
        })
        .filter(Boolean)

      // Merge images: inline plan images + separate gallery API images
      const inlineImages = normalizePlanImages(plan)
      const galleryEntry = galleryMap[planId]
      const galleryImages = galleryEntry ? normalizeImageList(galleryEntry.images) : []
      const mergedImages = [...new Set([...inlineImages, ...galleryImages])]

      return {
        id: planId,
        title,
        sharingLabel,
        description,
        monthlyPrice,
        originalPrice,
        amenities: [...new Set(planAmenities)],
        images: mergedImages.length > 0 ? mergedImages : [PROPERTY_IMAGE_FALLBACK]
      }
    })
    .filter(Boolean)
}

const inferGender = (rawProperty = {}) => {
  const explicit = toStringValue(rawProperty.gender ?? rawProperty.property_gender).toLowerCase()
  const haystack = `${explicit} ${toStringValue(rawProperty.property_name ?? rawProperty.name)}`.toLowerCase()

  if (/(lad(y|ies)|girl|female|women|womens|women's|girls)/.test(haystack)) {
    return 'female'
  }

  if (/(gent|boy|male|men|mens|men's|boys)/.test(haystack)) {
    return 'male'
  }

  return 'unisex'
}

const buildAreaValue = (rawProperty = {}) => {
  const rawLocation = rawProperty.location ?? {}
  return toStringValue(rawLocation.area ?? rawProperty.area, '')
}

const buildAddressValue = (rawProperty = {}, area = 'Unknown', city = 'Unknown') =>
  toStringValue(
    rawProperty.property_address ??
      rawProperty.fullAddress ??
      rawProperty.addressLine ??
      rawProperty.address ??
      rawProperty.location?.address,
    `${area}, ${city}`
  )

const buildPocketValue = (rawProperty = {}, area = 'OTHER') => {
  const sourcePocket = toStringValue(
    rawProperty.location?.pocket ??
      rawProperty.pocket ??
      rawProperty.pocketCode ??
      rawProperty.pocket_code
  )

  if (sourcePocket) {
    return sourcePocket.replace(/^pocket\s*/i, '').toUpperCase()
  }

  return slugify(area).toUpperCase() || 'OTHER'
}

const normalizeLocation = (rawProperty = {}) => {
  const city = toStringValue(rawProperty.city ?? rawProperty.location?.city, 'Unknown')
  const area = buildAreaValue(rawProperty)

  return {
    pocket: buildPocketValue(rawProperty, area || city),
    area,
    city,
    state: toStringValue(rawProperty.state ?? rawProperty.location?.state),
    country: toStringValue(rawProperty.country ?? rawProperty.location?.country),
    pincode: toStringValue(rawProperty.pincode ?? rawProperty.location?.pincode ?? rawProperty.zip_code),
    address: buildAddressValue(rawProperty, area || city, city)
  }
}

const normalizePricing = (rawProperty = {}) => {
  const rawPricing = rawProperty.pricing ?? rawProperty.price ?? {}
  const startFrom = toNumberValue(
    rawProperty.starts_from ??
      rawPricing.startFrom ??
      rawPricing.startingFrom ??
      rawPricing.monthly ??
      rawProperty.monthlyRent,
    0
  )

  return {
    monthly: toNumberValue(rawPricing.monthly ?? rawProperty.monthlyRent ?? startFrom, startFrom),
    daily: toNumberValue(rawPricing.daily ?? rawProperty.dailyRent, 0),
    startFrom,
    currency: toStringValue(rawPricing.currency, '₹')
  }
}

const normalizeRating = (rawProperty = {}) => ({
  stars: toNumberValue(rawProperty.ratings ?? rawProperty.rating?.stars ?? rawProperty.ratingValue, 0),
  viewCount: toNumberValue(rawProperty.no_views ?? rawProperty.rating?.viewCount, 0),
  reviewCount: toNumberValue(rawProperty.rating?.reviewCount, 0)
})

const normalizeAvailability = (rawProperty = {}) => ({
  rooms: toNumberValue(rawProperty.availability?.rooms ?? rawProperty.availableRooms, 0),
  beds: toNumberValue(rawProperty.availability?.beds ?? rawProperty.availableBeds, 0),
  isReadyForBooking: toBooleanValue(rawProperty.is_ready_for_booking, true)
})

const normalizeCoordinates = (rawProperty = {}) => {
  const latitude = toNumberValue(rawProperty.latitude ?? rawProperty.location?.latitude, null)
  const longitude = toNumberValue(rawProperty.longitude ?? rawProperty.location?.longitude, null)

  if (latitude === null || longitude === null) {
    return null
  }

  return { latitude, longitude }
}

const normalizeImages = (rawProperty = {}) => {
  const explicitImages = normalizeImageList(rawProperty.images)
  const galleryImages = extractGalleryImages(rawProperty)
  const fallbackImages = normalizeImageList([
    rawProperty.thumbnail_url,
    rawProperty.thumbnailUrl,
    rawProperty.thumbnail,
    rawProperty.image
  ])

  const mergedImages = [...new Set([...explicitImages, ...galleryImages, ...fallbackImages])]
  return mergedImages.length > 0 ? mergedImages : [PROPERTY_IMAGE_FALLBACK]
}

const buildStableSlug = (rawProperty = {}, fallbackName, index = 0) => {
  const explicitSlug = toStringValue(rawProperty.slug)
  if (explicitSlug) {
    return slugify(explicitSlug)
  }

  const baseSlug = slugify(fallbackName) || `property-${index + 1}`
  const idSeed = toStringValue(rawProperty.property_id ?? rawProperty.id)

  if (!idSeed) {
    return baseSlug
  }

  const compactId = slugify(idSeed).slice(-8)
  return compactId ? `${baseSlug}-${compactId}` : baseSlug
}

export const normalizeProperty = (rawProperty = {}, index = 0) => {
  const name = toStringValue(rawProperty.property_name ?? rawProperty.name, `Property ${index + 1}`)
  const slug = buildStableSlug(rawProperty, name, index)
  const location = normalizeLocation(rawProperty)
  const id = toStringValue(rawProperty.property_id ?? rawProperty.id, `${slug}-${index + 1}`)
  const availability = normalizeAvailability(rawProperty)
  const amenityGroups = normalizeAmenityGroups(rawProperty)
  const flattenedAmenities = flattenAmenityGroups(amenityGroups)
  const roomTypePlans = normalizeRoomTypePlans(rawProperty)

  return {
    propertyId: id,
    id,
    name,
    slug,
    location,
    propertyType: toStringValue(rawProperty.propertyType ?? rawProperty.type, 'PG'),
    gender: inferGender(rawProperty),
    pricing: normalizePricing(rawProperty),
    rating: normalizeRating(rawProperty),
    images: normalizeImages(rawProperty),
    thumbnailUrl: toImageUrl(rawProperty.thumbnail_url ?? rawProperty.thumbnailUrl) || '',
    facilities: toStringArray(rawProperty.facilities, flattenedAmenities),
    inclusions: toStringArray(rawProperty.inclusions, []),
    amenities: flattenedAmenities,
    amenityGroups,
    sharingOptions: toNumberArray(
      rawProperty.sharingOptions ?? rawProperty.sharing ?? rawProperty.sharing_options,
      DEFAULT_SHARING_OPTIONS
    ),
    featured: toBooleanValue(rawProperty.featured, false),
    hotSale: toBooleanValue(rawProperty.hotSale ?? rawProperty.hot_sale, false),
    discount: toNumberValue(rawProperty.discount, 0),
    availability,
    isReadyForBooking: availability.isReadyForBooking,
    coordinates: normalizeCoordinates(rawProperty),
    descriptionHtml: normalizeHtmlString(rawProperty.description),
    propertyRulesHtml: normalizeHtmlString(rawProperty.property_rules),
    cancellationPolicyHtml: normalizeHtmlString(rawProperty.cancellation_policy),
    nearByPlaceGroups: normalizeNearByPlaceGroups(rawProperty),
    roomTypePlans
  }
}

export const normalizePropertyCollection = (payload) => {
  const collection = Array.isArray(payload) ? payload : []
  const seenSlugs = new Set()

  return collection
    .map((item, index) => normalizeProperty(item, index))
    .filter((property, index) => {
      if (!property.slug) {
        return false
      }

      if (!seenSlugs.has(property.slug)) {
        seenSlugs.add(property.slug)
        return true
      }

      const nextSlug = `${property.slug}-${index + 1}`
      if (seenSlugs.has(nextSlug)) {
        return false
      }

      property.slug = nextSlug
      seenSlugs.add(nextSlug)
      return true
    })
}
